import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import MascotPlaceholder from "@/components/MascotPlaceholder";
import { trackEvent } from "@/lib/analytics";
import { ArrowLeft, X } from "@phosphor-icons/react";

export default function Quiz() {
  const { t, lang, updateAnonState } = useApp();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [i, setI] = useState(0);
  const [showIntro, setShowIntro] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Load quiz questions
  useEffect(() => {
    api.get(`/content/quiz?lang=${lang}`).then(r => setQuestions(r.data));
  }, [lang]);

  const q = questions[i];
  const progress = questions.length ? Math.round((i / questions.length) * 100) : 0;

  const exit = () => navigate("/");

  const startQuiz = () => setShowIntro(false);

  const pick = async (value) => {
    const next = { ...answers, [q.id]: value };
    setAnswers(next);
    // NOTE: We deliberately do NOT persist partial answers. Leaving mid-quiz
    // and returning should always restart from scratch.

    if (i < questions.length - 1) {
      setI(i + 1);
    } else {
      setSubmitting(true);
      const res = await api.post(`/quiz/submit?lang=${lang}`, { answers: next });
      await updateAnonState({ quiz_result: res.data });
      // Fire the analytics event first, navigate ONLY after Plausible has
      // sent (or a safety timeout fires). Otherwise the request is often
      // aborted by the immediate React Router navigation.
      trackEvent(
        "Quiz Completed",
        { outcome: res.data?.key || "unknown" },
        () => navigate("/quiz/risultato", { state: { profile: res.data } }),
      );
    }
  };

  const back = () => setI(Math.max(0, i - 1));

  if (!questions.length) {
    return <div className="max-w-2xl mx-auto p-8 text-ink-soft">{t.common.loading}</div>;
  }

  return (
    <div className="max-w-2xl mx-auto px-5 py-8 space-y-6">
      {/* Header row: title + exit link (always available) */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-terracotta font-semibold font-sans">
            {t.quiz.title}
          </p>
          {!showIntro && (
            <p className="text-ink-soft text-sm font-sans mt-1">
              {t.quiz.progress(i + 1, questions.length)}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <MascotPlaceholder emotion="thinking" src="/assets/boardy-pensieroso.png" />
          <button
            data-testid="quiz-exit"
            onClick={exit}
            aria-label={t.quiz.exit}
            className="w-10 h-10 rounded-full border border-border bg-surface-card text-ink-soft hover:text-ink hover:bg-surface-alt transition-colors flex items-center justify-center"
          >
            <X size={18} weight="bold" />
          </button>
        </div>
      </div>

      {/* Progress bar shown once past the intro */}
      {!showIntro && (
        <Progress
          data-testid="quiz-progress"
          value={progress}
          className="h-3 mb-8 bg-terracotta/15 [&>div]:bg-terracotta"
        />
      )}

      <AnimatePresence mode="wait">
        {showIntro ? (
          <motion.section
            key="intro"
            data-testid="quiz-intro"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="bg-surface-card rounded-3xl border border-border p-6 sm:p-8 shadow-[0_4px_20px_rgba(61,64,91,0.06)]"
          >
            <h1 data-testid="quiz-intro-title" className="font-display font-black text-3xl sm:text-4xl text-ink tracking-tight leading-tight">
              {t.quiz.title}
            </h1>
            <p className="mt-4 text-ink text-base sm:text-lg leading-relaxed font-sans">
              {t.quiz.introBody}
            </p>
            <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
              <Button
                data-testid="quiz-intro-start"
                onClick={startQuiz}
                className="rounded-full h-12 px-6 bg-terracotta hover:bg-terracotta-hover text-white font-sans font-bold"
              >
                {t.quiz.introStart}
              </Button>
              <button
                data-testid="quiz-intro-exit"
                onClick={exit}
                className="text-sm text-ink-soft underline underline-offset-4 hover:text-ink font-sans transition-colors"
              >
                {t.quiz.exit}
              </button>
            </div>
          </motion.section>
        ) : (
          <motion.div
            key={q.id}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <h2 data-testid="quiz-question" className="font-display font-extrabold text-2xl sm:text-3xl text-ink tracking-tight leading-tight mb-6">
              {q.prompt}
            </h2>

            <div className="space-y-3">
              {q.options.map(o => (
                <button
                  key={o.value}
                  data-testid={`quiz-option-${q.id}-${o.value}`}
                  onClick={() => pick(o.value)}
                  disabled={submitting}
                  className="w-full text-left rounded-2xl border border-border bg-surface-card px-5 py-4 hover:border-terracotta hover:bg-terracotta/5 transition-all active:scale-[0.99] font-sans text-ink"
                >
                  {o.label}
                </button>
              ))}
            </div>

            <div className="mt-8 flex items-center justify-between">
              <Button
                data-testid="quiz-back"
                variant="ghost"
                size="sm"
                onClick={back}
                disabled={i === 0}
                className="rounded-full text-ink-soft"
              >
                <ArrowLeft size={16} weight="bold" className="mr-1" /> {t.common.back}
              </Button>
              {submitting && <div className="text-sm text-ink-soft font-sans">{t.quiz.submitting}</div>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
